import React, { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { useDispatch, useSelector } from "react-redux";

import { Toaster } from "react-hot-toast";
import toastAlert from "./components/Items/ToastReact";
import { jwtDecode } from "jwt-decode";
import "./App.css";

// _________________________________
import i18n from "i18next";
import LanguageContext from "./LanguageContext";

import { getProfileData } from "./app/features/user";
import { logOutUser } from "./app/features/auth";
import { getCurrentLocation, resetLocation } from "./app/features/location";
import { resolvePlanTier } from "./config/subscriptionConfig";
import CustomLoader from "./components/Items/CustomLoader";
import { getMyLastSubscription } from "./services/stripeService";
import { applyThemeColors } from "./config/themeColors";
import ImpersonationBanner from "./components/ui/ImpersonationBanner";

// Code-split heavy routes with lazy loading to minimize initial bundle size
const HomePage = lazy(() => import("./pages/HomePage"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfileSecurity = lazy(() => import("./pages/Profile/Security"));
const Generator = lazy(() => import("./pages/Generator"));
const Login = lazy(() => import("./pages/Login/Login"));
const Signup = lazy(() => import("./pages/Signup/Signup"));
const ErrorPage = lazy(() => import("./pages/404ErrorPage"));
const VerifyAndUpdate = lazy(() => import("./pages/SubAdmins/VerifyAndUpdate"));
const StripeSuccess = lazy(() => import("./pages/StripeSuccess"));
const AdminRoutes = lazy(() => import("./admin/routes/AdminRoutes"));

function App() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.auth);

  useEffect(() => {
    applyThemeColors();
  }, []);
  const { isFetching } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);

  const [theme, colorMode] = useMode();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogOut = async () => {
    try {
      dispatch(logOutUser());
      dispatch(resetLocation());
      navigate("/");
      return;
    } catch (error) {
      toastAlert("error", "Something went wrong");
    }
  };

  const getUserDetails = async () => {
    try {
      setIsLoading(true);
      const decodeToken = jwtDecode(token);

      const currentTime = Math.floor(Date.now() / 1000);

      if (decodeToken.exp < currentTime) {
        handleLogOut();
      }

      const userId = decodeToken.id;

      await dispatch(getProfileData(userId)).unwrap();
      await dispatch(getCurrentLocation()).unwrap();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      getUserDetails();
    }
  }, [token]);

  // Fetch last subscription and persist plan info in localStorage
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!token) return;
      try {
        const resp = await getMyLastSubscription(token);
        const sub = resp?.data?.subscription ?? null;
        if (sub) {
          const resolvedPlan = resolvePlanTier(sub, user);

          const payload = {
            ...sub,
            plan: resolvedPlan,
            activatedAt: sub.start_date || sub.created_at || new Date().toISOString(),
            expiresAt: sub.expires_at || null,
            status: sub.status || null,
          };
          localStorage.setItem("subscription", JSON.stringify(payload));
        } else {
          localStorage.removeItem("subscription");
        }
      } catch (e) {
        // ignore errors
      }
    };

    fetchSubscription();
  }, [token, user]);

  if (isLoading || isFetching) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CustomLoader />
      </div>
    );
  }

  return (
    <>
      <LanguageContext.Provider value={changeLanguage}>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Suspense
              fallback={
                <div
                  style={{
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CustomLoader />
                </div>
              }
            >
              <ImpersonationBanner />
              <Routes>
                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/generator" element={<Generator />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/security" element={<ProfileSecurity />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/stripe-success" element={<StripeSuccess />} />
                <Route path="/verify-and-update-password" element={<VerifyAndUpdate />} />
                {/* Admin Portal Routes */}
                <Route path="/admin/*" element={<AdminRoutes />} />

                <Route path="*" element={<ErrorPage />} />
              </Routes>
            </Suspense>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </LanguageContext.Provider>

      {/* // )} */}
      <div>
        <Toaster position="top-center" />
      </div>
    </>
  );
}

export default App;
